import { useMemo, useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react"
import { Send, Paperclip, Smile, Mic, ImageIcon, FileText, Camera, X, Reply, Pencil, Trash2, MapPin, Music, Phone, Play, Video, Film } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { LinkPreview, Message, Participant } from "./types"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { acceptAttributeFor, checkFileForUpload, getUploadPolicy } from "@/lib/upload-policy"
import { useSendMessage } from "./hooks/useSendMessage"
import { useTyping } from "./hooks/useTyping"
import { useAttachmentUpload, type UploadRequest } from "./hooks/useAttachmentUpload"
import { useVoiceRecorder } from "./hooks/useVoiceRecorder"
import { CameraCaptureDialog } from "./camera-capture-dialog"
import type { Attachment } from "./services/messenger.wire"
import { nameForEncodedImage } from "./services/media-probe"
import { MAX_ATTACHMENTS_PER_MESSAGE } from "./attachments"
import { AvatarPhoto } from "./avatar-photo"

type CropHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w"

interface Box { x: number; y: number; w: number; h: number }

/** Smallest crop we allow, in on-screen px — below this the handles overlap. */
const MIN_CROP = 40

/**
 * Where the image is ACTUALLY painted inside its container.
 *
 * The preview uses `object-contain`, so unless the aspect ratios match exactly
 * the image is letterboxed and does not fill the box. Mapping crop coordinates
 * against the container instead of this rect is what makes a crop come out
 * offset from what the user selected.
 */
function renderedImageBox(img: HTMLImageElement, container: HTMLElement): Box {
  const cw = container.clientWidth
  const ch = container.clientHeight
  const nw = img.naturalWidth
  const nh = img.naturalHeight
  if (!nw || !nh) return { x: 0, y: 0, w: cw, h: ch }
  const scale = Math.min(cw / nw, ch / nh)
  const w = nw * scale
  const h = nh * scale
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h }
}

/** Resize `rect` by dragging `handle`, keeping the opposite edge anchored. */
function resizeRect(rect: Box, handle: CropHandle, dx: number, dy: number, bounds: Box): Box {
  let { x, y, w, h } = rect
  const right = x + w
  const bottom = y + h

  if (handle.includes("w")) {
    x = Math.min(Math.max(bounds.x, x + dx), right - MIN_CROP)
    w = right - x
  }
  if (handle.includes("e")) {
    w = Math.min(Math.max(MIN_CROP, w + dx), bounds.x + bounds.w - x)
  }
  if (handle.includes("n")) {
    y = Math.min(Math.max(bounds.y, y + dy), bottom - MIN_CROP)
    h = bottom - y
  }
  if (handle.includes("s")) {
    h = Math.min(Math.max(MIN_CROP, h + dy), bounds.y + bounds.h - y)
  }
  return { x, y, w, h }
}

const CROP_HANDLES: Array<{ id: CropHandle; className: string; cursor: string }> = [
  { id: "nw", className: "-left-1.5 -top-1.5", cursor: "nwse-resize" },
  { id: "n", className: "left-1/2 -translate-x-1/2 -top-1.5", cursor: "ns-resize" },
  { id: "ne", className: "-right-1.5 -top-1.5", cursor: "nesw-resize" },
  { id: "e", className: "-right-1.5 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
  { id: "se", className: "-right-1.5 -bottom-1.5", cursor: "nwse-resize" },
  { id: "s", className: "left-1/2 -translate-x-1/2 -bottom-1.5", cursor: "ns-resize" },
  { id: "sw", className: "-left-1.5 -bottom-1.5", cursor: "nesw-resize" },
  { id: "w", className: "-left-1.5 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
]

/** Only mobile honours `capture` on a file input; desktop needs getUserMedia. */
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
}

const EMOJI_DATA: Record<string, string[]> = {
  "😊": ["😀","😃","😄","😁","😆","😅","😂","🤣","😊","😇","🙂","😉","😍","🥰","😘","😋","😎","🤩","🥳","😏","😒","😞","😔","😟","😕","🙁","😣","😖","😫","😩","🥺","😢","😭","😤","😠","😡","🤬","😳","🥵","🥶","😱","😨","😰","😥","😓","🤗","🤔","🫡","🤫","😶","😑","😬","🙄","😯","😲","😵","😴"],
  "👋": ["👍","👎","👌","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","👇","☝️","✋","🖐️","🖖","👋","🤚","💪","🤝","🙏","✍️","👏","🙌","🤲","🤜","🤛","✊","👊","🫶"],
  "❤️": ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💯","🔥","✨","⭐","🌟","💫","🎉","🎊","🎈","🎁","🏆","🥇","🌈"],
  "🐶": ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🐔","🐧","🐦","🦆","🦅","🦉","🐺","🐴","🦄","🐝","🦋","🐌","🐞","🐜","🐢","🐍","🦎","🐙","🦑","🦀","🐠","🐟","🐬","🐳","🦈","🐘","🦒"],
  "🍎": ["🍎","🍊","🍋","🍌","🍉","🍇","🍓","🫐","🍒","🍑","🥭","🍍","🥥","🥝","🍅","🍆","🥑","🥦","🌽","🌶️","🥕","🍞","🥐","🧀","🥚","🍳","🥞","🌭","🍔","🍟","🍕","🌮","🍜","🍣","🍦","🍩","🍪","🎂","🍰","🍫","🍬","🍭","☕","🍵","🧋","🍺","🥂"],
}

type AttachmentType = "photo" | "video" | "document" | "camera" | "audio-file" | "contact" | "location"

const ATTACHMENT_OPTIONS: Array<{ icon: typeof ImageIcon; labelKey: string; color: string; type: AttachmentType }> = [
  { icon: ImageIcon, labelKey: "presence.photo",    color: "bg-violet-500/10 text-violet-600",   type: "photo" },
  // The video input and its handler already existed and nothing ever opened
  // them, so a video could only be sent through "Document" — which sends it as
  // `kind: document` and renders it as a file row instead of a player.
  { icon: Film,      labelKey: "presence.video",    color: "bg-indigo-500/10 text-indigo-600",   type: "video" },
  { icon: FileText,  labelKey: "presence.document", color: "bg-blue-500/10 text-blue-600",       type: "document" },
  { icon: Camera,    labelKey: "presence.camera",   color: "bg-emerald-500/10 text-emerald-600", type: "camera" },
  { icon: Music,     labelKey: "presence.audio",    color: "bg-pink-500/10 text-pink-600",       type: "audio-file" },
  { icon: Phone,     labelKey: "presence.contact",  color: "bg-amber-500/10 text-amber-600",     type: "contact" },
  { icon: MapPin,    labelKey: "presence.location", color: "bg-red-500/10 text-red-600",         type: "location" },
]

type PendingFileType = "image" | "video" | "file" | "audio-file"

interface PendingFile {
  id: string
  file: File
  url: string
  type: PendingFileType
}

interface MessageInputProps {
  /**
   * CDT-5b — when provided, the input uses the real `useSendMessage` /
   * `useTyping` / `useAttachmentUpload` hooks against this conversation.
   * When absent, falls back to the legacy `onSend`/`onSendFile` mock
   * callbacks so other call sites (if any) keep working.
   */
  conversationId?: string
  onSend: (payload: { text: string; linkPreview?: LinkPreview; imageDataUrl?: string }) => void
  onSendAudio?: (durationSeconds: number) => void
  onSendFile?: (file: File, type: PendingFileType) => void
  replyTo?: Message | null
  onCancelReply?: () => void
  /** Message being edited. Set => the composer switches to edit mode: it
   *  carries the existing text, shows a banner, and Send saves instead of
   *  sending a new message. Editing replaced a `window.prompt`, which put the
   *  page's domain in front of the user and looked nothing like the rest of
   *  the app. */
  editingMessage?: Message | null
  onCancelEdit?: () => void
  /** Resolve true when the edit was saved, so the composer knows to close. */
  onSubmitEdit?: (text: string) => Promise<boolean>
  placeholder?: string
  participants?: Participant[]
  isGroup?: boolean
  /** `conversationId` is a broadcast LIST — send fans out to its recipients. */
  isBroadcast?: boolean
  /** Fired after a successful broadcast fan-out. */
  onBroadcastSent?: () => void
}

export interface MessageInputRef {
  openImageCropWithFile: (file: File) => void
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(function MessageInput(
  { conversationId, onSend, onSendAudio, onSendFile, replyTo, onCancelReply, editingMessage, onCancelEdit, onSubmitEdit, placeholder, participants = [], isGroup = false, isBroadcast = false, onBroadcastSent },
  ref
) {
  // CDT-5b — real send / typing / upload hooks. Each falls back gracefully
  // when `conversationId` is absent so legacy mock callers still work.
  const { send, isSending } = useSendMessage(conversationId ?? null, { isBroadcast, onBroadcastSent })
  const { notifyTyping }    = useTyping(conversationId ?? null)
  const { uploadFiles, isUploading, clear: clearUploads } = useAttachmentUpload()
  const t = useTranslation()
  const inputPlaceholder = placeholder ?? t("presence.messagePlaceholder")
  const [text, setText] = useState("")
  const [showAttachments, setShowAttachments] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [emojiCategory, setEmojiCategory] = useState("😊")
  const recorder = useVoiceRecorder()
  const { isRecording, seconds: recordingSecs, waveform } = recorder
  const [cameraOpen, setCameraOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const emojiRef = useRef<HTMLDivElement>(null)

  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)

  const [pendingLinkPreview, setPendingLinkPreview] = useState<LinkPreview | null>(null)
  const [pendingImageSrc, setPendingImageSrc] = useState<string | null>(null)
  const [pendingImageCropped, setPendingImageCropped] = useState<string | null>(null)
  /** The picked file, kept so an uncropped send ships the ORIGINAL bytes rather
   *  than a canvas re-encode (which turns a small JPEG into a huge PNG). */
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  /** Crop is opt-in. Picking a photo previews it whole; you only get a crop
   *  rectangle after asking for one. */
  const [cropEnabled, setCropEnabled] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  const cropContainerRef = useRef<HTMLDivElement>(null)
  const cropImgRef = useRef<HTMLImageElement>(null)
  const cropDragRef = useRef<null | {
    startX: number
    startY: number
    rect: { x: number; y: number; w: number; h: number }
    /** 'move', or which corner/edge is being dragged. */
    mode: CropHandle | 'move'
  }>(null)

  // Pending non-image files (video, doc, audio-file)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  // Files staged for this message (cropper picture included), kept current for
  // handlers that resume after an await.
  const stagedCountRef = useRef(0)
  stagedCountRef.current = pendingFiles.length + (pendingImageFile ? 1 : 0)

  // The document picker's filter comes from the server's policy, so it is empty
  // (any file) until that has loaded.
  const [documentAccept, setDocumentAccept] = useState("")
  useEffect(() => {
    void getUploadPolicy().then(() => setDocumentAccept(acceptAttributeFor("attachment")))
  }, [])

  // Hidden file input refs
  const photoInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)
  const audioFileInputRef = useRef<HTMLInputElement>(null)

  /** Preview a picked image. Opens whole — crop is opt-in from the dialog. */
  const openImageCropWithFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    setPendingImageSrc(url)
    setPendingImageFile(file)
    setPendingImageCropped(null)
    setCropRect(null)
    setCropEnabled(false)
    setImageDialogOpen(true)
  }, [])

  /** Seed a crop rect covering ~80% of the painted image (not the container —
   *  see `renderedImageBox`), centred. */
  const startCropping = useCallback(() => {
    const el = cropContainerRef.current
    const img = cropImgRef.current
    if (!el || !img) return
    const box = renderedImageBox(img, el)
    const size = Math.floor(Math.min(box.w, box.h) * 0.8)
    setCropRect({
      x: Math.round(box.x + (box.w - size) / 2),
      y: Math.round(box.y + (box.h - size) / 2),
      w: size,
      h: size,
    })
    setCropEnabled(true)
  }, [])

  useImperativeHandle(ref, () => ({ openImageCropWithFile }), [openImageCropWithFile])

  useEffect(() => {
    if (!showEmojiPicker) return
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setShowEmojiPicker(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showEmojiPicker])

  const startRecording = async () => {
    const ok = await recorder.start()
    if (!ok && recorder.error) {
      toast.warning({ title: recorder.error })
    }
  }

  const cancelRecording = () => recorder.cancel()

  // A voice note is an attachment like any other: upload the clip, then send it
  // with `kind: 'voice'` so recipients get the waveform bubble rather than a
  // generic audio-file row (both are `audio/*` — only the kind separates them).
  const sendRecording = async () => {
    const clip = await recorder.stop()
    if (!clip) return

    if (conversationId) {
      try {
        const uploaded = await uploadFiles([{ file: clip.file, kind: 'voice' }])
        if (uploaded.length === 0) return
        const withDuration = uploaded.map(a => ({ ...a, durationMs: a.durationMs ?? clip.durationMs }))
        await send({ attachments: withDuration })
        clearUploads()
      } catch {
        // useSendMessage / useAttachmentUpload surface their own error state.
      }
      return
    }
    onSendAudio?.(Math.round(clip.durationMs / 1000))
  }

  const handleCameraCapture = (file: File) => {
    openImageCropWithFile(file)
  }

  const resetAll = () => {
    setText("")
    setPendingLinkPreview(null)
    setPendingImageSrc(null)
    setPendingImageCropped(null)
    setPendingImageFile(null)
    setPendingFiles([])
    setImageDialogOpen(false)
    setCropRect(null)
    setCropEnabled(false)
    setShowAttachments(false)
    setShowEmojiPicker(false)
    setMentionOpen(false)
    setMentionQuery("")
    setMentionStart(null)
    if (textareaRef.current) textareaRef.current.style.height = "auto"
  }

  /**
   * Convert a `data:` URL to a `File` so the cropped image can ride the
   * standard multipart upload path. Used only when `pendingImageCropped`
   * is present (image picker → crop → send).
   */
  const dataUrlToFile = async (dataUrl: string, name: string): Promise<File> => {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    const type = blob.type || "image/png"
    return new File([blob], nameForEncodedImage(name, type), { type })
  }

  /**
   * Move a picked image into the crop slot so it can be edited.
   *
   * There is one crop slot, and only the image occupying it had a ✎ — so the
   * first of a multi-selection could be cropped and the rest could not. This
   * swaps: whatever is in the slot goes back to the list, the chosen one takes
   * its place.
   *
   * The outgoing image is stashed as its CROPPED bytes when it has been
   * cropped, so editing a second picture does not quietly undo the first.
   */
  const cropPendingImage = async (pf: PendingFile) => {
    if (pendingImageFile) {
      const file = pendingImageCropped
        ? await dataUrlToFile(pendingImageCropped, pendingImageFile.name)
        : pendingImageFile
      const url = URL.createObjectURL(file)
      setPendingFiles(prev => [...prev, { id: `${Date.now()}-${file.name}`, file, url, type: "image" }])
    }
    setPendingFiles(prev => prev.filter(p => p.id !== pf.id))
    openImageCropWithFile(pf.file)
  }

  // Entering edit mode carries the existing text into the box, so the user
  // corrects a typo rather than retyping the message. Keyed on the message id
  // so re-renders while editing never stomp on what is being typed.
  const editingIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!editingMessage) {
      editingIdRef.current = null
      return
    }
    if (editingIdRef.current === editingMessage.id) return
    editingIdRef.current = editingMessage.id
    setText(editingMessage.text ?? "")
    const el = textareaRef.current
    if (el) {
      el.focus()
      // Caret at the end — the common case is appending or fixing the tail.
      const end = (editingMessage.text ?? "").length
      requestAnimationFrame(() => el.setSelectionRange(end, end))
    }
  }, [editingMessage])

  // Starting a reply means the next thing the user does is type it, so the
  // box takes focus the same way it does for an edit.
  const replyToId = replyTo?.id ?? null
  useEffect(() => {
    if (!replyToId || editingMessage) return
    const el = textareaRef.current
    if (!el) return
    // After the frame, so the menu that offered Reply has finished closing.
    const raf = requestAnimationFrame(() => el.focus())
    return () => cancelAnimationFrame(raf)
  }, [replyToId, editingMessage])

  const cancelEdit = () => {
    setText("")
    editingIdRef.current = null
    onCancelEdit?.()
  }

  const handleSend = async () => {
    const body = text.trim()

    // ── Edit mode: Send saves the existing message instead of posting a new
    //    one. Attachments are deliberately not part of an edit — the server
    //    only accepts a new body.
    if (editingMessage) {
      if (!body || !onSubmitEdit) return
      if (body === (editingMessage.text ?? "").trim()) {
        cancelEdit()
        return
      }
      const saved = await onSubmitEdit(body)
      if (saved) cancelEdit()
      return
    }

    const hasContent = body || pendingLinkPreview || pendingImageFile || pendingFiles.length > 0
    if (!hasContent) return
    if (isSending || isUploading) return

    // ── CDT-5b real path: conversationId is set, route through the hooks.
    if (conversationId) {
      try {
        // Each request carries its kind so the recipient's bubble is chosen
        // from intent, not from a mime guess. Everything else the bubble needs
        // (dimensions, duration, video poster) is probed during upload.
        const requests: UploadRequest[] = []
        // Cropped => the canvas re-encode; otherwise the ORIGINAL file, so an
        // uncropped photo isn't needlessly rasterised and inflated.
        if (pendingImageCropped) {
          const ext = pendingImageCropped.startsWith('data:image/png') ? 'png' : 'jpg'
          requests.push({
            file: await dataUrlToFile(pendingImageCropped, `image-${Date.now()}.${ext}`),
            kind: 'photo',
          })
        } else if (pendingImageFile) {
          requests.push({ file: pendingImageFile, kind: 'photo' })
        }
        for (const pf of pendingFiles) {
          requests.push({
            file: pf.file,
            // `image` must reach the wire as `photo`, or the recipient's bubble
            // renders a picture as a document row.
            kind:
              pf.type === 'image' ? 'photo'
              : pf.type === 'video' ? 'video'
              : pf.type === 'audio-file' ? 'audio'
              : 'document',
          })
        }

        let attachments: Attachment[] | undefined
        if (requests.length > 0) {
          const uploaded = await uploadFiles(requests)
          // Every upload failed — don't send a silently empty message.
          if (uploaded.length === 0 && !body) return
          if (uploaded.length > 0) attachments = uploaded
        }

        const result = await send({
          body: body || undefined,
          replyTo: replyTo?.id,
          attachments,
        })
        if (result) {
          resetAll()
          // The reply target lives in the parent, so `resetAll` cannot reach
          // it — it only clears this composer's own state. Without this the
          // chip stayed above the input after sending, and the next message
          // typed went out as another reply to a message the user had already
          // answered and moved on from. The legacy mock path below never had
          // the bug because its parent clears the target in `onSend`.
          onCancelReply?.()
        }
      } catch {
        // useSendMessage / useAttachmentUpload set their own error state;
        // we silently swallow at the UI layer so the composer stays usable.
      }
      return
    }

    // ── Legacy mock path: keep behaviour identical for any caller that
    //    forgot to pass conversationId.
    if (body || pendingLinkPreview || pendingImageCropped || pendingImageSrc) {
      onSend({ text: body, linkPreview: pendingLinkPreview ?? undefined, imageDataUrl: pendingImageCropped ?? pendingImageSrc ?? undefined })
    }
    for (const pf of pendingFiles) onSendFile?.(pf.file, pf.type)
    resetAll()
  }

  // ── Attachment button handler ──────────────────────────────────────────
  const handleAttachmentOption = (type: AttachmentType) => {
    setShowAttachments(false)
    switch (type) {
      case "photo":
        photoInputRef.current?.removeAttribute("capture")
        photoInputRef.current?.click()
        break
      case "camera":
        // `capture` only does anything on mobile; on desktop the input degrades
        // to a plain file picker, which would make Camera a duplicate of Photo.
        if (isMobileDevice() && photoInputRef.current) {
          photoInputRef.current.setAttribute("capture", "environment")
          photoInputRef.current.click()
        } else {
          setCameraOpen(true)
        }
        break
      case "document":
        docInputRef.current?.click()
        break
      case "video":
        videoInputRef.current?.click()
        break
      case "audio-file":
        audioFileInputRef.current?.click()
        break
      case "contact":
      case "location":
        toast.info({ title: t("common.comingSoon"), description: t("presence.featureComingSoon") })
        break
    }
  }

  // ── File select handlers ───────────────────────────────────────────────
  /**
   * Photos, however many were picked.
   *
   * The input has always been `multiple`, so the OS picker let you choose a
   * whole set — and this handler then read `files[0]` and dropped the rest,
   * silently. Every other attachment type already iterates.
   *
   * The first image still opens the crop dialog, because cropping is a
   * single-image affordance and losing it for one photo would be a worse trade.
   * The rest join the same pending list videos and documents use, so they send
   * together in one message.
   */
  /**
   * Keep only the files that belong to the option they were picked from.
   *
   * `accept` on a file input is a hint the picker offers, not a rule it
   * enforces — every OS dialog has an "All files" escape, and some ignore it
   * outright. Nothing downstream checked, so a video chosen under Photos was
   * filed as an image and handed to the image cropper, which cannot read it:
   * the crop dialog came up empty and the send did nothing until it was
   * dismissed and tried again. An image chosen under Documents went out as a
   * document and arrived as a file card with no preview.
   *
   * Rejected files are named. "Some files were skipped" leaves the reader
   * counting thumbnails to work out which.
   */
  const keepOfKind = (files: File[], accept: (mime: string) => boolean, what: string): File[] => {
    const kept = files.filter((f) => accept(f.type))
    const rejected = files.filter((f) => !accept(f.type))
    if (rejected.length > 0) {
      const names = rejected.map((f) => f.name)
      toast.error({
        title: `Only ${what} can be attached here`,
        description: names.length === 1 ? names[0] : `Skipped: ${names.join(", ")}`,
      })
    }
    return kept
  }

  /**
   * Drop files the server would refuse (type, size, name, contents) and name
   * each one with the reason, so the failure is at pick time rather than after
   * an upload. Passes everything through when the policy is unreachable.
   */
  const keepAllowed = async (files: File[]): Promise<File[]> => {
    const kept: File[] = []
    for (const file of files) {
      const check = await checkFileForUpload(file, "attachment")
      if (check.ok) kept.push(file)
      else toast.error({ title: check.message, description: file.name })
    }
    return kept
  }

  /**
   * Trim a pick to what the message has room for, and say so.
   *
   * Counted against everything already staged — the cropper's picture and the
   * files under the composer — because they all leave in the same message.
   */
  const keepWithinLimit = (files: File[]): File[] => {
    // Read after the policy await, from a ref: the closure's own counts are
    // from before it, so two quick picks would each see an empty composer.
    const room = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - stagedCountRef.current)
    stagedCountRef.current += Math.min(files.length, room)
    if (files.length <= room) return files
    toast.error({
      title: `Up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message`,
      description:
        room === 0
          ? "Send these first, then attach the rest."
          : `Keeping the first ${room}.`,
    })
    return files.slice(0, room)
  }

  const isImage = (mime: string) => mime.startsWith("image/")
  const isVideo = (mime: string) => mime.startsWith("video/")
  const isAudio = (mime: string) => mime.startsWith("audio/")
  /** A document is anything that is not one of the media kinds, each of which
   *  has its own option and its own bubble. */
  const isDocument = (mime: string) => !isImage(mime) && !isVideo(mime) && !isAudio(mime)

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Filtered before anything is done with them — the cropper below cannot
    // read a video, and handing it one is what broke the composer.
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ""
    const [first, ...rest] = keepWithinLimit(await keepAllowed(keepOfKind(picked, isImage, "photos")))
    if (first) openImageCropWithFile(first)
    for (const file of rest) {
      const url = URL.createObjectURL(file)
      setPendingFiles(prev => [...prev, { id: `${Date.now()}-${file.name}`, file, url, type: "image" }])
    }
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ""
    keepWithinLimit(await keepAllowed(keepOfKind(picked, isVideo, "videos"))).forEach(file => {
      const url = URL.createObjectURL(file)
      setPendingFiles(prev => [...prev, { id: `${Date.now()}-${file.name}`, file, url, type: "video" }])
    })
  }

  const handleDocSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Anything that is not a photo, video or audio file. Those three have
    // their own options and their own bubbles; sent as documents they arrive
    // as file cards with no preview and no player.
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ""
    keepWithinLimit(await keepAllowed(keepOfKind(picked, isDocument, "documents"))).forEach(file => {
      const url = URL.createObjectURL(file)
      setPendingFiles(prev => [...prev, { id: `${Date.now()}-${file.name}`, file, url, type: "file" }])
    })
  }

  const handleAudioFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ""
    keepWithinLimit(await keepAllowed(keepOfKind(picked, isAudio, "audio files"))).forEach(file => {
      const url = URL.createObjectURL(file)
      setPendingFiles(prev => [...prev, { id: `${Date.now()}-${file.name}`, file, url, type: "audio-file" }])
    })
  }

  const removePendingFile = (id: string) => {
    setPendingFiles(prev => {
      const pf = prev.find(f => f.id === id)
      if (pf) URL.revokeObjectURL(pf.url)
      return prev.filter(f => f.id !== id)
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // ── Mention helpers ────────────────────────────────────────────────────
  const applyMention = (p: Participant) => {
    if (mentionStart === null) return
    const el = textareaRef.current
    const end = el?.selectionStart ?? text.length
    const before = text.slice(0, mentionStart)
    const after = text.slice(end)
    const insert = `@${p.name} `
    const next = `${before}${insert}${after}`
    setText(next)
    setMentionOpen(false)
    setMentionQuery("")
    setMentionStart(null)
    setMentionIndex(0)
    requestAnimationFrame(() => {
      if (!textareaRef.current) return
      const pos = (mentionStart ?? 0) + insert.length
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(pos, pos)
    })
  }

  // ── Emoji helpers ──────────────────────────────────────────────────────
  /**
   * Insert at the caret and hand focus back to the textarea.
   *
   * The picker's buttons must not keep focus. A focused <button> activates on
   * Enter, so leaving focus there meant Enter re-fired the emoji button —
   * appending the same emoji again — instead of reaching the textarea's
   * onKeyDown and sending. Same reason the mention popup uses onMouseDown +
   * preventDefault: the caret never leaves the composer.
   */
  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const next = `${text.slice(0, start)}${emoji}${text.slice(end)}`
    setText(next)
    requestAnimationFrame(() => {
      if (!textareaRef.current) return
      const pos = start + emoji.length
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(pos, pos)
    })
  }

  // ── Link preview ──────────────────────────────────────────────────────
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/i
  const normalizeUrl = (raw: string) => (raw.startsWith("http") ? raw : `https://${raw}`)
  const getHost = (u: string) => { try { return new URL(u).hostname } catch { return "" } }

  // ── Keyboard ──────────────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionOpen) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionItems.length - 1)); return }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return }
      if (e.key === "Enter")     { e.preventDefault(); const item = mentionItems[mentionIndex]; if (item) applyMention(item); return }
      if (e.key === "Escape")    { e.preventDefault(); setMentionOpen(false); setMentionQuery(""); setMentionStart(null); return }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
    e.target.style.height = "auto"
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"

    // CDT-5b — sender-side typing push. `notifyTyping` is 3-s leading-edge
    // throttled inside the hook, so calling it on every keystroke is fine.
    if (e.target.value && conversationId) notifyTyping()

    if (!isGroup || participants.length === 0) { setMentionOpen(false); setMentionQuery(""); setMentionStart(null); return }

    const caret = e.target.selectionStart ?? e.target.value.length
    const upto = e.target.value.slice(0, caret)
    const at = upto.lastIndexOf("@")
    if (at < 0) { setMentionOpen(false); setMentionQuery(""); setMentionStart(null); return }

    const prevChar = at === 0 ? " " : upto[at - 1]
    if (prevChar && /[\w]/.test(prevChar)) { setMentionOpen(false); setMentionQuery(""); setMentionStart(null); return }

    const token = upto.slice(at + 1)
    if (token.includes(" ") || token.includes("\n")) { setMentionOpen(false); setMentionQuery(""); setMentionStart(null); return }

    setMentionOpen(true)
    setMentionStart(at)
    setMentionQuery(token)
    setMentionIndex(0)
  }

  const mentionItems = useMemo(() => {
    if (!isGroup) return []
    const q = mentionQuery.trim().toLowerCase()
    return q ? participants.filter(p => p.name.toLowerCase().includes(q)) : participants
  }, [isGroup, mentionQuery, participants])

  const mentionGroups = useMemo(() => ({
    admins: mentionItems.filter(p => p.isAdmin),
    members: mentionItems.filter(p => !p.isAdmin),
  }), [mentionItems])

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items)
    const imgItem = items.find(it => it.kind === "file" && it.type.startsWith("image/"))
    if (imgItem) { e.preventDefault(); const f = imgItem.getAsFile(); if (f) openImageCropWithFile(f); return }
    const textData = e.clipboardData.getData("text")
    const m = textData.match(urlRegex)
    if (m?.[0]) {
      const normalized = normalizeUrl(m[0])
      const host = getHost(normalized)
      setPendingLinkPreview({ url: normalized, title: host || normalized, description: normalized, image: host ? `https://www.google.com/s2/favicons?domain=${host}&sz=128` : undefined })
    }
  }

  // ── Image crop ────────────────────────────────────────────────────────
  /**
   * Rasterise the selection. Coordinates are mapped through the *painted* image
   * rect, not the container, so a letterboxed preview still crops the region the
   * user actually drew. Output keeps the source mime where possible — PNG-ing a
   * photo multiplies its size several-fold.
   */
  const applyCrop = async (): Promise<boolean> => {
    if (!pendingImageSrc || !cropRect || !cropContainerRef.current || !cropImgRef.current) return false
    const img = cropImgRef.current
    const box = renderedImageBox(img, cropContainerRef.current)
    if (!box.w || !box.h) return false

    const scaleX = img.naturalWidth / box.w
    const scaleY = img.naturalHeight / box.h
    const sx = Math.max(0, Math.round((cropRect.x - box.x) * scaleX))
    const sy = Math.max(0, Math.round((cropRect.y - box.y) * scaleY))
    const sw = Math.max(1, Math.min(img.naturalWidth - sx, Math.round(cropRect.w * scaleX)))
    const sh = Math.max(1, Math.min(img.naturalHeight - sy, Math.round(cropRect.h * scaleY)))

    const canvas = document.createElement("canvas")
    canvas.width = sw; canvas.height = sh
    const ctx = canvas.getContext("2d")
    if (!ctx) return false
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)

    const mime = pendingImageFile?.type === "image/png" ? "image/png" : "image/jpeg"
    setPendingImageCropped(canvas.toDataURL(mime, 0.92))
    return true
  }

  /** Send the picked image as-is, no crop. */
  const keepWholeImage = () => {
    setPendingImageCropped(null)
    setCropEnabled(false)
    setCropRect(null)
    setImageDialogOpen(false)
  }

  const confirmCrop = async () => {
    if (!cropEnabled || !cropRect) { keepWholeImage(); return }
    if (await applyCrop()) setImageDialogOpen(false)
  }

  const beginCropDrag = (e: React.PointerEvent, mode: CropHandle | "move") => {
    if (!cropRect) return
    e.stopPropagation()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    cropDragRef.current = { startX: e.clientX, startY: e.clientY, rect: cropRect, mode }
  }

  const onCropPointerMove = (e: React.PointerEvent) => {
    const drag = cropDragRef.current
    if (!drag || !cropContainerRef.current || !cropImgRef.current) return
    const bounds = renderedImageBox(cropImgRef.current, cropContainerRef.current)
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY

    if (drag.mode === "move") {
      // Movement is clamped to the painted image so you can't drag the
      // selection out over the letterbox bars.
      const x = Math.min(bounds.x + bounds.w - drag.rect.w, Math.max(bounds.x, drag.rect.x + dx))
      const y = Math.min(bounds.y + bounds.h - drag.rect.h, Math.max(bounds.y, drag.rect.y + dy))
      setCropRect({ ...drag.rect, x, y })
      return
    }
    setCropRect(resizeRect(drag.rect, drag.mode, dx, dy, bounds))
  }

  const onCropPointerUp = () => { cropDragRef.current = null }

  const fmtSecs = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`

  const getFileIcon = (type: PendingFileType) => {
    if (type === "image") return ImageIcon
    if (type === "video") return Video
    if (type === "audio-file") return Music
    return FileText
  }

  const previewImage = pendingImageCropped ?? (pendingImageFile ? pendingImageSrc : null)
  // While editing, the button stays Send whatever the box holds — offering
  // to record a voice note in the middle of correcting a typo is not a
  // thing anyone wants, and an empty box is handled by `handleSend`.
  const hasContent = !!editingMessage || text.trim() || previewImage || pendingFiles.length > 0

  /**
   * Everything currently attached, as one flat list for the preview strip.
   *
   * One image at a time lives in the crop slot (`pendingImageFile` + its
   * possibly-cropped preview) while the rest sit in `pendingFiles`. That split
   * is how cropping works, not something the user chose, so it gets flattened
   * here and the strip renders every entry the same way. The crop slot holds
   * the first image picked, so putting it first keeps selection order.
   */
  const previewItems: Array<{
    id: string
    url: string
    name: string
    size: number
    type: PendingFileType
    onCrop: () => void | Promise<void>
    onRemove: () => void
  }> = [
    ...(previewImage && pendingImageFile
      ? [{
          id: "crop-slot",
          url: previewImage,
          name: pendingImageFile.name,
          size: pendingImageFile.size,
          type: "image" as PendingFileType,
          onCrop: () => setImageDialogOpen(true),
          onRemove: () => {
            setPendingImageSrc(null)
            setPendingImageFile(null)
            setPendingImageCropped(null)
            setCropRect(null)
          },
        }]
      : []),
    ...pendingFiles.map(pf => ({
      id: pf.id,
      url: pf.url,
      name: pf.file.name,
      size: pf.file.size,
      type: pf.type,
      onCrop: () => cropPendingImage(pf),
      onRemove: () => removePendingFile(pf.id),
    })),
  ]

  return (
    <div className="border-t border-border bg-card px-4 py-2.5 shrink-0">
      {/* Edit banner — same shape as the reply banner below, because it is the
          same idea: the composer is aimed at an existing message and the user
          needs to see which one, and get out. */}
      {editingMessage && (
        <div className="flex items-center gap-3 mb-3 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl">
          <div className="w-1 h-10 bg-amber-500 rounded-full shrink-0" />
          <Pencil className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-600 mb-0.5">{t("presence.editingMessage")}</p>
            <p className="text-xs text-muted-foreground truncate">{editingMessage.text}</p>
          </div>
          <button
            onClick={cancelEdit}
            aria-label={t("common.cancel")}
            className="p-1.5 rounded-full hover:bg-muted text-muted-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reply banner */}
      {replyTo && !editingMessage && (
        <div className="flex items-center gap-3 mb-3 px-4 py-3 bg-primary/8 border border-primary/20 rounded-2xl">
          <div className="w-1 h-10 bg-primary rounded-full shrink-0" />
          <Reply className="w-4 h-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-primary mb-0.5">{replyTo.senderName ?? t("common.you")}</p>
            <p className="text-xs text-muted-foreground truncate">
              {replyTo.text || (replyTo.type === "audio" ? `🎵 ${t("presence.voiceMessage")}` : replyTo.type === "image" ? `📷 ${t("presence.photo")}` : replyTo.type === "file" ? `📄 ${t("presence.document")}` : "")}
            </p>
          </div>
          <button onClick={onCancelReply} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Selected attachments — ONE list, one card shape.
          The image occupying the crop slot used to render as a large framed
          thumbnail above the rest, with a text "Remove" beside the heading that
          looked like it cleared everything and in fact dropped only that one.
          Same treatment for all of them now; which image happens to hold the
          crop slot is an implementation detail and should not be visible. */}
      {previewItems.length > 0 && (
        <div className="mb-3">
          <p className="text-sm font-medium text-muted-foreground mb-2">
            {t("presence.selectedItems")} ({previewItems.length}/20)
          </p>
          <div className="flex flex-wrap gap-2">
            {previewItems.map(item => {
              const Icon = getFileIcon(item.type)
              return (
                <div key={item.id} className="relative flex items-center gap-2 bg-muted/60 rounded-xl px-3 py-2 max-w-[220px] border border-border">
                  {item.type === "image" ? (
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-muted">
                      <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => void item.onCrop()}
                        title={t("presence.crop")}
                        aria-label={`${t("presence.crop")} — ${item.name}`}
                        className="absolute inset-0 grid place-items-center bg-black/45 text-white text-xs opacity-0 hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      >
                        ✎
                      </button>
                    </div>
                  ) : item.type === "video" ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-black relative">
                      <video src={item.url} className="w-full h-full object-cover" muted />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="w-4 h-4 text-white fill-white" />
                      </div>
                    </div>
                  ) : (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${item.type === "audio-file" ? "bg-pink-500/10 text-pink-600" : "bg-blue-500/10 text-blue-600"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate text-foreground">{item.name}</p>
                    <p className="text-[10px] text-muted-foreground">{formatFileSize(item.size)}</p>
                  </div>
                  <button
                    onClick={item.onRemove}
                    aria-label={`${t("common.remove")} — ${item.name}`}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-sm transition-transform hover:scale-110"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Link preview */}
      {pendingLinkPreview && (
        <div className="mb-3 rounded-2xl border border-border bg-muted/30 overflow-hidden">
          <div className="flex items-start gap-3 p-3">
            {pendingLinkPreview.image ? (
              <img src={pendingLinkPreview.image} alt="" className="w-10 h-10 rounded-xl bg-card object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-card" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{pendingLinkPreview.title}</div>
              <div className="text-xs text-muted-foreground truncate">{pendingLinkPreview.url}</div>
            </div>
            <button onClick={() => setPendingLinkPreview(null)} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground" aria-label={t("presence.removeLinkPreview")}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Emoji picker */}
      {showEmojiPicker && (
        <div ref={emojiRef} className="mb-2 bg-popover border border-border rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 border-b border-border overflow-x-auto">
            {Object.keys(EMOJI_DATA).map(cat => (
              <button key={cat} type="button" onMouseDown={ev => ev.preventDefault()} onClick={() => setEmojiCategory(cat)}
                className={`text-base w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-colors ${emojiCategory === cat ? "bg-primary/10" : "hover:bg-muted"}`}
              >{cat}</button>
            ))}
            <button type="button" onMouseDown={ev => ev.preventDefault()} onClick={() => { setShowEmojiPicker(false); textareaRef.current?.focus() }} className="ml-auto p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-9 gap-0 p-2 max-h-36 overflow-y-auto">
            {(EMOJI_DATA[emojiCategory] ?? []).map(emoji => (
              <button key={emoji} type="button" onMouseDown={ev => ev.preventDefault()} onClick={() => insertEmoji(emoji)}
                className="text-xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              >{emoji}</button>
            ))}
          </div>
        </div>
      )}

      {/* Attachment grid */}
      {showAttachments && (
        <div className="grid grid-cols-3 gap-2 mb-3 p-3 bg-muted/40 rounded-2xl">
          {ATTACHMENT_OPTIONS.map(({ icon: Icon, labelKey, color, type }) => (
            <button
              key={labelKey}
              onClick={() => handleAttachmentOption(type)}
              className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl hover:bg-card transition-colors"
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-muted-foreground font-medium">{t(labelKey)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Recording bar */}
      {isRecording ? (
        <div className="flex items-center gap-3 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-950/20 dark:to-red-900/20 rounded-2xl p-3 border border-red-200/50 dark:border-red-800/30">
          <button onClick={cancelRecording} className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive transition-all hover:scale-105 shrink-0">
            <Trash2 className="w-5 h-5" />
          </button>
          <div className="flex-1 flex items-center gap-3 bg-card/80 dark:bg-black/20 rounded-xl px-4 py-3 backdrop-blur-sm">
            <div className="relative">
              <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse shrink-0 block" />
              <span className="absolute inset-0 w-3 h-3 rounded-full bg-red-500 animate-ping opacity-75" />
            </div>
            <div className="flex items-end gap-[3px] flex-1 h-6">
              {waveform.map((h, i) => (
                <div key={i} className="w-[3px] rounded-full bg-gradient-to-t from-red-500 to-red-400 transition-all duration-150 ease-out" style={{ height: `${Math.max(h * 100, 15)}%`, animationDelay: `${i * 50}ms` }} />
              ))}
            </div>
            <span className="text-sm font-mono text-red-600 dark:text-red-400 font-semibold shrink-0">{fmtSecs(recordingSecs)}</span>
          </div>
          <button onClick={sendRecording} className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0 hover:bg-primary/90 transition-all active:scale-95 shadow-lg">
            <Send className="w-5 h-5" />
          </button>
        </div>
      ) : (
        /* Normal input row */
        <div className="flex items-end gap-2">
          <button
            onClick={() => { setShowAttachments(v => !v); setShowEmojiPicker(false) }}
            className={`p-2.5 rounded-full transition-all shrink-0 mb-0.5 ${showAttachments ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}
          >
            {showAttachments ? <X className="w-5 h-5" /> : <Paperclip className="w-5 h-5" />}
          </button>

          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={inputPlaceholder}
              rows={1}
              className="w-full bg-muted/80 backdrop-blur-sm rounded-3xl pl-5 pr-12 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none leading-relaxed max-h-[120px] overflow-y-auto border border-transparent focus:border-primary/20 focus:bg-background transition-all"
            />

            {/* Mention popup */}
            {mentionOpen && mentionItems.length > 0 && (
              <div className="absolute left-0 right-0 bottom-full mb-2 rounded-2xl border border-border bg-popover shadow-xl overflow-hidden">
                {mentionGroups.admins.length > 0 && (
                  <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground bg-muted/30">{t("presence.admins")}</div>
                )}
                {mentionGroups.admins.map(p => (
                  <button key={p.id} type="button" onMouseDown={ev => ev.preventDefault()} onClick={() => applyMention(p)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors ${mentionItems[mentionIndex]?.id === p.id ? "bg-muted" : ""}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold overflow-hidden shrink-0">
                      <AvatarPhoto
                        src={p.imageUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        fallback={p.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                        contactId={p.id}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground">{t("presence.adminRole")}</div>
                    </div>
                  </button>
                ))}
                {mentionGroups.members.length > 0 && (
                  <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground bg-muted/30">{t("presence.members")}</div>
                )}
                {mentionGroups.members.map(p => (
                  <button key={p.id} type="button" onMouseDown={ev => ev.preventDefault()} onClick={() => applyMention(p)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors ${mentionItems[mentionIndex]?.id === p.id ? "bg-muted" : ""}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold overflow-hidden shrink-0">
                      <AvatarPhoto
                        src={p.imageUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        fallback={p.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                        contactId={p.id}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground">{t("presence.memberRole")}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => { setShowEmojiPicker(v => !v); setShowAttachments(false) }}
              className={`absolute right-3 bottom-2.5 p-1.5 transition-all rounded-full ${showEmojiPicker ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            >
              <Smile className="w-5 h-5" />
            </button>
          </div>

          {hasContent ? (
            <button onClick={handleSend} className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0 mb-0.5 hover:bg-primary/90 transition-all active:scale-95 shadow-lg">
              <Send className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={startRecording} className="p-2.5 rounded-full hover:bg-red-50 dark:hover:bg-red-950/20 text-muted-foreground hover:text-red-500 transition-all shrink-0 mb-0.5 relative group">
              <Mic className="w-5 h-5" />
              <span className="absolute -top-10 right-0 bg-popover border border-border text-xs px-3 py-1.5 rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">{t("presence.recordVoice")}</span>
            </button>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={photoInputRef}     type="file" accept="image/*"                                                          multiple className="hidden" onChange={handlePhotoSelect} />
      <input ref={videoInputRef}     type="file" accept="video/*"                                                          multiple className="hidden" onChange={handleVideoSelect} />
      {/* The server's allowlist is the picker filter; the handler checks each
          file again, since every picker can be widened to "All files". */}
      <input ref={docInputRef}       type="file" accept={documentAccept || undefined}                                                                    multiple className="hidden" onChange={handleDocSelect} />
      <input ref={audioFileInputRef} type="file" accept="audio/*"                                                          multiple className="hidden" onChange={handleAudioFileSelect} />

      <CameraCaptureDialog open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />

      {/* Image preview — crop is opt-in */}
      <Dialog open={imageDialogOpen} onOpenChange={o => { setImageDialogOpen(o); if (!o) cropDragRef.current = null }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{cropEnabled ? t("presence.cropMode") : t("presence.addImage")}</DialogTitle>
          </DialogHeader>
          <div className="w-full">
            <div ref={cropContainerRef} className="relative w-full aspect-[16/9] bg-black/80 rounded-xl overflow-hidden">
              {pendingImageSrc && (
                <img ref={cropImgRef} src={pendingImageSrc} alt="" className="absolute inset-0 w-full h-full object-contain select-none" draggable={false} />
              )}
              {cropEnabled && cropRect && (
                <div
                  className="absolute inset-0 touch-none"
                  onPointerMove={onCropPointerMove}
                  onPointerUp={onCropPointerUp}
                  onPointerCancel={onCropPointerUp}
                >
                  <div
                    className="absolute border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] rounded-sm cursor-move"
                    style={{ left: cropRect.x, top: cropRect.y, width: cropRect.w, height: cropRect.h }}
                    onPointerDown={e => beginCropDrag(e, "move")}
                  >
                    {CROP_HANDLES.map(h => (
                      <span
                        key={h.id}
                        role="presentation"
                        onPointerDown={e => beginCropDrag(e, h.id)}
                        style={{ cursor: h.cursor }}
                        className={`absolute w-3 h-3 rounded-full bg-white border border-black/30 shadow ${h.className}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {cropEnabled ? t("presence.cropHint") : t("presence.sendWholeHint")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImageDialogOpen(false); setPendingImageSrc(null); setPendingImageFile(null) }}>
              {t("common.cancel")}
            </Button>
            {cropEnabled ? (
              <Button variant="outline" onClick={() => { setCropEnabled(false); setCropRect(null) }}>
                {t("presence.cancelCrop")}
              </Button>
            ) : (
              <Button variant="outline" onClick={startCropping} disabled={!pendingImageSrc}>
                {t("presence.crop")}
              </Button>
            )}
            <Button onClick={() => void confirmCrop()} disabled={!pendingImageSrc}>
              {t("common.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
})
